// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

/* ============================================================
   ================= NODE  =====================================
   ============================================================ */

export const PARENT_HEADER_HEIGHT = 60;
const PARENT_WIDTH = 250;
export const CHILD_HEIGHT = 112.5;
const CHILD_WIDTH = 200;
const CHILD_SPACING = 16;
export const GAP = 50;
const NUM_COLUMNS = 2;
export const HANDLE_HEIGHT = 18;

// TODO : Refactor the state to match the equipment group mapping in the backend to avoid this normaliation step and sorting group  to add equipment group mapping to equipment inside group and chidlren(parent ID that breaks the react flow UI if not in the right order)

export const normalizeGroup = (group) => {
  const equipment =
    group.equipment_group_mappings?.map((m) => ({
      ...m.company_equipment,
      direct_member: m.direct_member,
    })) ?? [];
  return {
    ...group,
    equipment,
  };
};

// helper function to avoid react flow issues with group being rendered before its children in the fetch equipment groups from backend
const sortGroupsByHierarchy = (groups = []) => {
  const map = new Map(groups.map((g) => [g.equip_group_uuid, g]));
  const visited = new Set();
  const result = [];

  const visit = (group) => {
    if (visited.has(group.equip_group_uuid)) {
      return;
    }

    if (group.parent_uuid) {
      const parent = map.get(group.parent_uuid);
      if (parent) {
        visit(parent);
      }
    }

    visited.add(group.equip_group_uuid);
    result.push(group);
  };

  groups.forEach(visit);

  return result;
};

/* ================= FACILITY NODE (EQUIPMENT GROUP AT TOP LEVEL) ================= */
export const createFacilityNode = ({ group }) => ({
  id: group.equip_group_uuid,
  type: "facility",
  position: { x: 0, y: 0 },
  draggable: false,
  selectable: false,
  data: {
    state: group,
    isFacility: true,
  },
  style: {
    display: "none",
  },
});

/* ================= EQUIPMENT GROUP NODE ================= */
export const createGroupNode = ({
  group,
  index,
  prevNode,
  facilityEquipmentGroupUuid,
  equipmentList,
  groupsList,
  activeNodeId,
}) => {
  const isChild = Boolean(
    group.parent_uuid && group.parent_uuid !== facilityEquipmentGroupUuid,
  );

  const defaultPosition = isChild
    ? {
        x: 25,
        y:
          PARENT_HEADER_HEIGHT +
          CHILD_SPACING +
          index * (CHILD_HEIGHT + CHILD_SPACING),
      }
    : {
        x: (index % NUM_COLUMNS) * (PARENT_WIDTH + GAP),
        y: Math.floor(index / NUM_COLUMNS) * (PARENT_HEADER_HEIGHT + GAP),
      };

  return {
    id: group.equip_group_uuid,
    type: "equipmentGroup",
    position: prevNode?.position ?? defaultPosition,
    draggable: true,
    ...(isChild && {
      parentId: group.parent_uuid,
      extent: "parent",
      hidden: prevNode?.hidden ?? true,
    }),
    data: {
      subGroup: isChild,
      state: group,
      width: PARENT_WIDTH,
      height: PARENT_HEADER_HEIGHT,
      isOpen: prevNode?.data?.isOpen ?? false,
      isPlaceholder: prevNode?.data?.isPlaceholder ?? false,
      isActive: group.equip_group_uuid === activeNodeId,
      handles: (() => {
        const handles = getEquipmentGroupHandles(
          group,
          equipmentList,
          groupsList,
        );
        return {
          inputs: handles.filter((h) => h.connectionType === "input"),
          outputs: handles.filter((h) => h.connectionType === "output"),
        };
      })(),
    },
    className: prevNode?.className,
  };
};

/* ================= EQUIPMENT NODE ================= */
export const createEquipmentNode = ({
  equip,
  index,
  groupId,
  prevNode,
  equipmentList,
  activeNodeId,
}) => {
  const isChild = Boolean(groupId);

  const defaultPosition = isChild
    ? {
        x: 25,
        y:
          PARENT_HEADER_HEIGHT +
          CHILD_SPACING +
          index * (CHILD_HEIGHT + CHILD_SPACING),
      }
    : { x: 600, y: index * (CHILD_HEIGHT + GAP) };

  const parentChanged = prevNode && prevNode.parentId !== groupId;

  const position =
    !parentChanged && prevNode?.position ? prevNode.position : defaultPosition;

  return {
    id: equip.co_equip_uuid,
    type: "equipment",
    position,
    draggable: true,
    ...(isChild && {
      parentId: parentChanged ? groupId : (prevNode?.parentId ?? groupId),
      extent: "parent",
      hidden: parentChanged ? true : (prevNode?.hidden ?? true),
    }),
    data: {
      state: equip,
      width: CHILD_WIDTH,
      height: CHILD_HEIGHT,
      isPlaceholder: prevNode?.data?.isPlaceholder ?? false,
      isActive: equip.co_equip_uuid === activeNodeId,
      handles: (() => {
        const handles = getEquipmentHandles(equip, equipmentList);
        return {
          inputs: handles.filter((h) => h.connectionType === "input"),
          outputs: handles.filter((h) => h.connectionType === "output"),
        };
      })(),
    },
    className: prevNode?.className,
  };
};

/* ================= RECURSIVE NODES BUILDER ================= */
const buildNodes = ({
  groups = [],
  prevMap,
  facilityEquipmentGroupUuid,
  equipmentList,
  activeNodeId,
}) => {
  if (!groups.length) {
    return [];
  }

  // Helper to remove duplicates based on key
  const removeDuplicates = (items, key) => [
    ...new Map(items.map((item) => [item[key], item])).values(),
  ];

  // Separate facility group from other equipment groups
  const facilityGroup = groups.find(
    (g) => g?.equip_group_uuid === facilityEquipmentGroupUuid,
  );

  const equipmentGroups = groups.filter(
    (g) => g?.equip_group_uuid !== facilityEquipmentGroupUuid,
  );

  // Facility node and its equipment(standalone equipment)
  const facilityNodes = facilityGroup
    ? [
        createFacilityNode({ group: facilityGroup }),
        ...removeDuplicates(
          facilityGroup.equipment?.filter((e) => e.direct_member) ?? [],
          "co_equip_uuid",
        ).map((equip, i) =>
          createEquipmentNode({
            equip,
            index: i,
            groupId: null,
            prevNode: prevMap.get(equip.co_equip_uuid),
            equipmentList,
            activeNodeId,
          }),
        ),
      ]
    : [];

  // Equipment group nodes and their equipment(nested equipment inside groups)
  const groupNodes = equipmentGroups.flatMap((group, index) => {
    const groupNode = createGroupNode({
      group,
      index,
      prevNode: prevMap.get(group.equip_group_uuid),
      facilityEquipmentGroupUuid,
      equipmentList,
      groupsList: groups,
      activeNodeId,
    });

    const equipmentNodes = removeDuplicates(
      group.equipment?.filter((e) => e.direct_member) ?? [],
      "co_equip_uuid",
    ).map((equip, i) =>
      createEquipmentNode({
        equip,
        index: i,
        groupId: group.equip_group_uuid,
        prevNode: prevMap.get(equip.co_equip_uuid),
        equipmentList,
        activeNodeId,
      }),
    );

    return [groupNode, ...equipmentNodes];
  });

  return [...facilityNodes, ...groupNodes];
};

/* ================= EXPAND INITIAL NODES ================= */
export const expandInitialNodes = ({
  groups = [],
  previousNodes = [],
  facilityEquipmentGroupUuid,
  equipmentList,
  activeNodeId,
}) => {
  const prevMap = new Map((previousNodes ?? []).map((n) => [n.id, n]));
  const sortedGroups = sortGroupsByHierarchy(groups);
  return buildNodes({
    groups: sortedGroups,
    prevMap,
    facilityEquipmentGroupUuid,
    equipmentList,
    activeNodeId,
  });
};

/* ============================================================
   ================= EQUIPMENT HANDLE  ====================
   ============================================================ */

const getEquipmentDirectionMap = (equipment) => {
  const props = equipment?.current_config?.properties || {};
  return {
    input: props.inputs || null,
    output: props.outputs || null,
  };
};

/* ---------- Create Handle---------- */
const createHandle = ({
  nodeId,
  connectionType,
  id,
  config = null,
  decorative = false,
  children = null,
}) => {
  const rfType = connectionType === "input" ? "target" : "source";

  return {
    id: decorative ? `${nodeId}_${connectionType}` : id,
    nodeId,
    connectionType,
    rfType,
    decorative,
    displayName: config?.display_name || id,
    protocol: decorative ? null : config?.protocol || null,
    color: config?.color || "",
    ...(config?.sourceName ? { sourceName: config.sourceName } : {}),
    ...(config?.targetName ? { targetName: config.targetName } : {}),
    ...(decorative && children ? { children } : {}),
  };
};

/* ---------- Find attached handles from output destinations ---------- */

// Reusable: find handle ID in connected equipment that points to targetNodeId
export const findConnectedHandleId = (
  equipment,
  targetNodeId,
  type = "input",
) => {
  const settings = equipment?.current_config?.settings || {};

  // If looking for an input connection, check destinations
  // If looking for an output connection, check sources
  const map = type === "input" ? settings.destinations : settings.sources;
  if (!map) {
    return null;
  }

  for (const [childName, child] of Object.entries(map)) {
    const childUuid =
      child?.uuid?.equip_setting_val_uuid?.value || child?.uuid?.value;
    if (childUuid === targetNodeId) {
      return childName;
    }
  }

  return null;
};

/* ---------- Attach connection / destination to handle ---------- */
export const attachConnectionToHandle = (handle, settings, equipmentList) => {
  if (!handle || !settings) {
    return handle;
  }

  // Map depends on handle type
  const map =
    handle.connectionType === "input"
      ? settings.sources
      : settings.destinations;
  if (!map) {
    return handle;
  }

  const child = map[handle.id];
  if (!child) {
    return handle;
  }

  const connectedUuid =
    child?.uuid?.equip_setting_val_uuid?.value || child?.uuid?.value;
  if (!connectedUuid) {
    return handle;
  }

  const connectedEquip = equipmentList.find(
    (eq) => eq.co_equip_uuid === connectedUuid,
  );

  if (!connectedEquip) {
    return handle;
  }

  // Use reusable function to find the handle ID
  const connectedHandleId = findConnectedHandleId(
    connectedEquip,
    handle.nodeId,
    handle.connectionType,
  );

  if (!connectedHandleId) {
    return handle;
  }

  return {
    ...handle,
    ...(handle.connectionType === "input"
      ? {
          connection: {
            sourceNodeId: connectedUuid,
            sourceHandleId: connectedHandleId,
          },
          sourceName: connectedEquip.name,
        }
      : {
          destination: {
            targetNodeId: connectedUuid,
            targetHandleId: connectedHandleId,
          },
          targetName: connectedEquip.name,
        }),
  };
};

/* ---------- Equipment Handles ---------- */
export const getEquipmentHandles = (equipment, equipmentList) => {
  if (!equipment?.co_equip_uuid) {
    return [];
  }

  const nodeId = equipment.co_equip_uuid;
  const directions = getEquipmentDirectionMap(equipment);
  const settings = equipment?.current_config?.settings || {};

  return Object.entries(directions).flatMap(([connectionType, container]) => {
    if (!container) {
      return [];
    }

    // dynamically iterate over child nodes
    const handles = Object.entries(container)
      .map(([childKey, child]) => {
        // Dynamically get protocol and display_name from child
        const protocol = child?.protocol?.eprop_val_uuid?.value;
        const displayName =
          child?.display_name?.eprop_val_uuid?.value || childKey;

        if (!protocol) {
          return null;
        }

        const config = {
          display_name: displayName,
          protocol,
          color: getProtocolColor(protocol),
        };

        connectionType === "output"
          ? (config.sourceName = equipment.name)
          : (config.targetName = equipment.name);

        const handle = createHandle({
          nodeId,
          connectionType,
          id: childKey,
          config,
        });

        return attachConnectionToHandle(handle, settings, equipmentList);
      })
      .filter(Boolean)
      .sort((a, b) => a.id.localeCompare(b.id));

    // TODO : create a decorative handle if more than two handle with same protocols
    // decorative handle logic
    if (handles.length > 2) {
      return [
        createHandle({
          nodeId,
          connectionType,
          config: { display_name: `${equipment.name} (${connectionType})` },
          decorative: true,
          children: handles,
        }),
        ...handles,
      ];
    }

    return handles;
  });
};
/* ============================================================
   ================= GROUP HANDLE  ========================
   ============================================================ */

const groupHandlesByDirection = (handles) =>
  handles.reduce(
    (acc, handle) => {
      const key = `${handle.nodeId}:${handle.id}`;
      acc[handle.connectionType][key] = handle;
      return acc;
    },
    { input: {}, output: {} },
  );

export const getEquipmentGroupHandles = (group, equipmentList, groupList) => {
  if (!group?.equip_group_uuid) {
    return [];
  }

  const allHandles = group.equipment.flatMap((eq) =>
    getEquipmentHandles(eq, equipmentList),
  );

  const grouped = groupHandlesByDirection(allHandles);

  return Object.entries(grouped).map(([type, map]) => {
    const children = Object.values(map);

    const connectedChild =
      type === "input"
        ? children.find((h) => h.connection?.sourceNodeId)
        : children.find((h) => h.destination?.targetNodeId);

    const config = {
      display_name: `${group.name} (${type})`,
    };

    type === "output"
      ? (config.sourceName = group.name)
      : (config.targetName = group.name);

    const groupHandle = createHandle({
      nodeId: group.equip_group_uuid,
      connectionType: type,
      config,
      decorative: true,
      children,
    });

    // If a child is connected, propagate that connection to the group handle
    if (connectedChild) {
      const connectedEquipmentId =
        type === "input"
          ? connectedChild.connection?.sourceNodeId
          : connectedChild.destination?.targetNodeId;

      const parentGroup = groupList.find((g) =>
        g.equipment?.some((eq) => eq.co_equip_uuid === connectedEquipmentId),
      );

      if (parentGroup) {
        const oppositeType = type === "input" ? "output" : "input";

        if (type === "input") {
          // Inputs store connection
          groupHandle.connection = {
            sourceNodeId: parentGroup.equip_group_uuid,
            sourceHandleId: `${parentGroup.equip_group_uuid}_${oppositeType}`,
          };

          groupHandle.sourceName = parentGroup.name;
        }

        if (type === "output") {
          // Outputs MUST store destination
          groupHandle.connection = {
            targetNodeId: parentGroup.equip_group_uuid,
            targetHandleId: `${parentGroup.equip_group_uuid}_${oppositeType}`,
          };

          groupHandle.targetName = parentGroup.name;
        }
      }
    }

    return groupHandle;
  });
};
/* ============================================================
   ==========  CONNECTIONS LOGIC BETWEEN HANDLES =============
   ============================================================ */
export const isConnectionAllowed = (connection, currentNodes) => {
  const { source, sourceHandle, target, targetHandle } = connection;

  if (!source || !target || !sourceHandle || !targetHandle) {
    return false;
  }
  const sourceNode = currentNodes.find((n) => n.id === source);

  const targetNode = currentNodes.find((n) => n.id === target);

  if (!sourceNode || !targetNode) {
    return false;
  }

  // Equipment nodes connections must match protocol on handles
  if (sourceNode.type === "equipment" && targetNode.type === "equipment") {
    // Find the handle objects for source and target and compare their protocols
    const sourceHandleObj = sourceNode.data?.handles?.outputs?.find(
      (h) => h.id === sourceHandle,
    );

    const targetHandleObj = targetNode.data?.handles?.inputs?.find(
      (h) => h.id === targetHandle,
    );

    return sourceHandleObj?.protocol === targetHandleObj?.protocol;
  }

  // Equipment groups can only connect to other equipment groups
  if (
    sourceNode.type === "equipmentGroup" &&
    targetNode.type === "equipmentGroup"
  ) {
    const { outputs: sourceOutputs = [] } = sourceNode.data?.handles || {};
    const { inputs: targetInputs = [] } = targetNode.data?.handles || {};

    const sourceHandleObj = sourceOutputs.find((h) => h.id === sourceHandle);
    const targetHandleObj = targetInputs.find((h) => h.id === targetHandle);

    if (!sourceHandleObj || !targetHandleObj) {
      return false;
    }

    const sourceChildren = (sourceHandleObj.children || []).filter(
      (sc) => sc.protocol,
    );
    const targetChildren = (targetHandleObj.children || []).filter(
      (tc) => tc.protocol,
    );

    // Check both directions for safety
    const hasMatchingProtocol =
      sourceChildren.some((sc) =>
        targetChildren.some((tc) => sc.protocol === tc.protocol),
      ) ||
      targetChildren.some((tc) =>
        sourceChildren.some((sc) => tc.protocol === sc.protocol),
      );
    return hasMatchingProtocol;
  }
  // TODO : add case connection from equipment to equipment group if protocol match with equipment within the group

  return false;
};

// Build a set of used target handles based on current equipment in the facility to prevent multiple connections to the same handle when matching protocols between groups
const buildUsedTargetHandles = (
  equipmentInsideFacility = [],
  sourceOutputs = [],
) => {
  const used = new Set();
  for (const eq of equipmentInsideFacility) {
    const eqId = eq?.co_equip_uuid;
    for (const output of sourceOutputs) {
      if (output?.destination?.targetNodeId === eqId) {
        used.add(`${output.nodeId}:${output.id}`);
      }
    }
  }

  return used;
};

// Find matching protocols between equipment inside a group
export const getMatchingProtocols = (
  sourceOutputs = [],
  targetInputs = [],
  equipmentInsideFacility = [],
) => {
  const matches = [];

  const usedTargetGlobal = buildUsedTargetHandles(
    equipmentInsideFacility,
    sourceOutputs,
  );

  const usedSourceLocal = new Set();
  const usedTargetLocal = new Set(usedTargetGlobal);

  for (const sourcePort of sourceOutputs) {
    if (!sourcePort?.protocol) {
      continue;
    }

    const sourceKey = `${sourcePort.nodeId}:${sourcePort.id}`;

    if (usedSourceLocal.has(sourceKey)) {
      continue;
    }

    const compatibleTargets = targetInputs.filter(
      (t) =>
        t.protocol === sourcePort.protocol &&
        !usedTargetLocal.has(`${t.nodeId}:${t.id}`),
    );

    if (!compatibleTargets.length) {
      continue;
    }

    const selectedTarget = compatibleTargets[0];

    matches.push({
      protocol: sourcePort.protocol,
      sourceNodeId: sourcePort.nodeId,
      sourceHandle: sourcePort.id,
      targetNodeId: selectedTarget.nodeId,
      targetHandle: selectedTarget.id,
    });

    usedSourceLocal.add(sourceKey);
    usedTargetLocal.add(`${selectedTarget.nodeId}:${selectedTarget.id}`);
  }

  return matches;
};

/* ============================================================
   ================= EDGE / CONNECTIONS =======================
   ============================================================ */

const handleCache = new Map();

const getEquipmentHandlesWithCache = (equipment, equipmentList) => {
  if (!equipment?.co_equip_uuid) {
    return [];
  }

  const cacheKey = `${equipment.co_equip_uuid}_${equipment.last_updated}`;

  if (handleCache.has(cacheKey)) {
    return handleCache.get(cacheKey);
  }

  const handles = getEquipmentHandles(equipment, equipmentList);

  handleCache.set(cacheKey, handles);

  return handles;
};

export const findNodeData = (nodeId, equipmentList, groupList) => {
  const equipment = equipmentList.find((eq) => eq.co_equip_uuid === nodeId);
  if (equipment) {
    return { type: "equipment", data: equipment };
  }

  for (const group of groupList) {
    const found = group.equipment?.find(
      (eq) => eq.co_equip_uuid === nodeId && eq.direct_member,
    );

    if (found) {
      return { type: "equipment", data: found };
    }
  }

  const group = groupList.find((g) => g.equip_group_uuid === nodeId);

  if (group) {
    return { type: "group", data: group };
  }
  return null;
};

const getHandlesFromNodeData = (nodeData, equipmentList, allGroups) => {
  const { type, data } = nodeData;

  if (type === "equipment") {
    return getEquipmentHandlesWithCache(data, equipmentList);
  }

  if (type === "group") {
    return getEquipmentGroupHandles(data, equipmentList, allGroups).flatMap(
      (h) => [h, ...(h.children ?? [])],
    );
  }

  return [];
};

const getConnectedTargetHandles = (handles) =>
  handles.filter((h) => h.rfType === "target" && h.connection?.sourceNodeId);

const buildEdgeFromHandle = (handle) => {
  const { sourceNodeId, sourceHandleId } = handle?.connection || {};

  if (!sourceNodeId || !sourceHandleId) {
    return null;
  }

  return {
    id: `${sourceNodeId}_${sourceHandleId}_${handle.nodeId}_${handle.id}`,
    source: sourceNodeId,
    sourceHandle: sourceHandleId,
    target: handle.nodeId,
    targetHandle: handle.id,
    type: "custom",
    selectable: true,
    selected: false,
    interactionWidth: 75,
    label: `${handle.sourceName} → ${handle.targetName}\n${handle.protocol ? ` (${handle.protocol})` : ""}`,
    data: {
      color: handle.color,
      protocol: handle.protocol,
    },
  };
};

export const buildEdgesForNode = ({ nodeId, equipment, groups }) => {
  const nodeData = findNodeData(nodeId, equipment, groups);

  if (!nodeData) {
    return [];
  }

  const handles = getHandlesFromNodeData(nodeData, equipment, groups);

  const edges = getConnectedTargetHandles(handles)
    .map(buildEdgeFromHandle)
    .filter(Boolean);

  return edges;
};

/* ============================================================
   ================= NODE LAYOUT RECALCULATION ================
   ============================================================ */

export const isDescendant = (target, nodeId, currentNodes) => {
  let current = target;
  while (current?.parentId) {
    if (current.parentId === nodeId) {
      return true;
    }
    current = currentNodes.find((n) => n.id === current.parentId);
  }
  return false;
};

const getChildren = (nodes, parentId) =>
  nodes.filter((n) => n.parentId === parentId && !n.hidden);

const buildChildrenMap = (nodes) =>
  nodes.reduce((map, node) => {
    if (!node.parentId) {
      return map;
    }
    map.set(node.parentId, [...(map.get(node.parentId) || []), node]);
    return map;
  }, new Map());

const computeGroupHeight = (nodes, parentId, cache = new Map()) => {
  const childrenMap = buildChildrenMap(nodes);

  const stack = [{ id: parentId, state: "enter" }];

  while (stack.length) {
    const node = stack.pop();

    if (node.state === "enter") {
      stack.push({ id: node.id, state: "exit" });
      const children = childrenMap.get(node.id) || [];
      // Push open equipmentGroups for recursion
      children
        .filter((child) => child.type === "equipmentGroup" && child.data.isOpen)
        .forEach((child) => stack.push({ id: child.id, state: "enter" }));
    } else {
      const children = childrenMap.get(node.id) || [];

      const height = children.reduce((acc, child) => {
        const childHeight = child.data?.height ?? CHILD_HEIGHT;
        const openExtra =
          child.type === "equipmentGroup" && child.data.isOpen
            ? (cache.get(child.id) || PARENT_HEADER_HEIGHT) -
              PARENT_HEADER_HEIGHT
            : 0;

        return acc + childHeight + CHILD_SPACING + openExtra;
      }, PARENT_HEADER_HEIGHT);

      cache.set(node.id, height + (children.length ? CHILD_SPACING : 0));
    }
  }

  return cache.get(parentId) || PARENT_HEADER_HEIGHT;
};

/* -------------------------------------------------------
   layoutChildren optimized
------------------------------------------------------- */

const layoutChildren = (nodes, parentId) => {
  const children = getChildren(nodes, parentId);
  let y = PARENT_HEADER_HEIGHT + CHILD_SPACING;

  const updatedChildMap = new Map(
    children.map((child) => {
      const updated = {
        ...child,
        position: { ...child.position, y },
      };
      const childHeight = child.data?.height ?? CHILD_HEIGHT;
      y += childHeight + CHILD_SPACING;
      return [child.id, updated];
    }),
  );

  return nodes.map((n) => updatedChildMap.get(n.id) || n);
};

export const updateParentLayout = (nodes, nodeId) => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const currentNode = nodeMap.get(nodeId);
  if (!currentNode?.parentId) {
    return nodes;
  }

  const parentId = currentNode.parentId;

  const cache = new Map();
  const updatedParentHeight = computeGroupHeight(nodes, parentId, cache);

  let updatedNodes = nodes.map((n) =>
    n.id === parentId
      ? { ...n, data: { ...n.data, height: updatedParentHeight } }
      : n,
  );

  updatedNodes = layoutChildren(updatedNodes, parentId);

  return updatedNodes;
};

/* -------------------------------------------------------
   Toggle open / close with cache
------------------------------------------------------- */

const collectOpenDescendants = (nodes, parentId) => {
  const children = getChildren(nodes, parentId);
  let openDescendants = [];

  children.forEach((child) => {
    if (child.type === "equipmentGroup" && child.data.isOpen) {
      openDescendants.push(child);
      openDescendants = openDescendants.concat(
        collectOpenDescendants(nodes, child.id),
      );
    }
  });

  return openDescendants;
};

const setDescendantsHidden = (nodes, parentId, hidden, cache) => {
  const openChildren = collectOpenDescendants(nodes, parentId);

  if (hidden) {
    openChildren.forEach((child) => {
      nodes = toggleNode(nodes, child.id, false, cache);
    });
  }

  return nodes.map((n) => (n.parentId === parentId ? { ...n, hidden } : n));
};

export const toggleNode = (
  nodes,
  parentId,
  forceOpen = null,
  cache = new Map(),
) => {
  const parent = nodes.find((n) => n.id === parentId);
  if (!parent) {
    return nodes;
  }

  const isOpen = forceOpen !== null ? forceOpen : !parent.data.isOpen;

  // Update parent open state
  let updated = nodes.map((n) =>
    n.id === parentId ? { ...n, data: { ...n.data, isOpen } } : n,
  );

  // Update hidden state of descendants
  updated = setDescendantsHidden(updated, parentId, !isOpen, cache);

  // Layout children if opening
  const positioned = isOpen
    ? layoutChildren(updated, parentId, cache)
    : updated;

  // Compute new parent height
  const newHeight = isOpen
    ? computeGroupHeight(positioned, parentId, cache)
    : PARENT_HEADER_HEIGHT;

  // Apply new height to parent
  return positioned.map((n) =>
    n.id === parentId ? { ...n, data: { ...n.data, height: newHeight } } : n,
  );
};

/* ============================================================
   ================= EQUIPMENT PROPERTIES/SETTING MAPPING  =====
   ============================================================ */
export const updateConfigObject = (
  stateObj = {},
  payloadObj = {},
  path = "",
  context = "properties", // "properties" or "settings"
) => {
  Object.keys(payloadObj).forEach((key) => {
    const payloadValue = payloadObj[key];
    const currentPath = path ? `${path}.${key}` : key;

    if (!(key in stateObj)) {
      return;
    }

    const stateValue = stateObj[key];

    // Recurse into nested objects
    if (
      typeof payloadValue === "object" &&
      payloadValue !== null &&
      !Array.isArray(payloadValue)
    ) {
      updateConfigObject(stateValue, payloadValue, currentPath, context);
      return;
    }

    // Detect existing value key
    const valueKey =
      "equip_setting_val_uuid" in stateValue
        ? "equip_setting_val_uuid"
        : "eprop_val_uuid" in stateValue
          ? "eprop_val_uuid"
          : null;

    if (!valueKey) {
      // Create the appropriate key based on context propertie or ssetting
      if (context === "settings") {
        stateValue.equip_setting_val_uuid = { value: payloadValue };
      } else {
        stateValue.eprop_val_uuid = { value: payloadValue };
      }
      return;
    }

    // Update existing value container
    const valueContainer = stateValue[valueKey];
    stateValue[valueKey] = {
      ...valueContainer,
      value: payloadValue,
    };
  });
};

export const buildEquipmentTree = (obj, mode = "property") => {
  if (!obj || typeof obj !== "object") {
    return [];
  }

  const isProperty = mode === "property";

  const TYPE_KEY = isProperty ? "property_type" : "setting_type";
  const UUID_KEY = isProperty ? "eprop_uuid" : "equip_setting_uuid";
  const NAME_KEY = isProperty ? "property_name" : "setting_name";
  const LABEL_KEY = isProperty
    ? "property_display_name"
    : "setting_display_name";
  const VALUE_KEY = isProperty ? "eprop_val_uuid" : "equip_setting_val_uuid";
  const CHOICES_KEY = isProperty ? "property_choices" : "setting_choices";

  return Object.values(obj)
    .filter((item) => item && typeof item === "object" && item[TYPE_KEY])
    .map((item) => {
      // CONTAINER
      if (item[TYPE_KEY] === "Container") {
        const children = Object.values(item).filter(
          (child) => child && typeof child === "object" && child[TYPE_KEY],
        );

        const builtChildren = buildEquipmentTree(children, mode);

        return {
          type: "container",
          id: item[UUID_KEY],
          name: item[NAME_KEY],
          label: item[LABEL_KEY],
          children: builtChildren,
        };
      }

      // FIELD
      return {
        type: "field",
        id: item[UUID_KEY],
        name: item[NAME_KEY],
        label: item[LABEL_KEY],
        value: item[VALUE_KEY]?.value,
        propertyType: item[TYPE_KEY],
        options:
          item[TYPE_KEY] === "Choice"
            ? item[CHOICES_KEY]?.map((choice) => ({
                value: choice.value,
                label: choice.display_value,
              }))
            : undefined,
      };
    })
    .filter(Boolean);
};

/* ================= RETURN FORM TYPE FROM VALUE TYPE ================== */

export const mapValueTypeToFormType = (valueType) => {
  const mapping = {
    Boolean: "Checkbox",
    Integer: "Number",
    Float: "Number",
    String: "Input",
    UUID: "Input",
    "IP Address": "Input",
    Choice: "Select",
    Container: "Container",
  };

  return mapping[valueType] || "Input";
};

/* ----helper to get color edges/handles based on protocol------ */
const getProtocolColor = (protocol) => {
  const colors = {
    elevation_v5: "var(--color-info)",
    carriage_v5: "var(--color-primary)",
    PTZ_VISCA: "var(--color-info)",
    RTSP: "var(--color-success)",
    carriage_position: "var(--color-primary)",
    default: "var(--color-light)",
  };

  return colors[protocol] || colors.default;
};

/* ---------- helper to get color status ---------- */
// TODO :  move to utils file in web-utils (only ok don t match variant name with badge variant)
export const getColorStatus = (status) => {
  switch (status) {
    case "warning":
      return "warning";
    case "ok":
      return "success";
    case "error":
      return "error";
    default:
      return "mostudio";
  }
};
